import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header, Footer, MobileBottomNav } from '../components/Layout';
import { walletApi, paymentsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Wallet, Plus, ArrowUpRight, ArrowDownLeft, Sparkles, 
  DollarSign, Send, History, CreditCard, RefreshCw, X
} from 'lucide-react';

const WalletPage = () => {
  const [searchParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depositAmount, setDepositAmount] = useState(10);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showPayFast, setShowPayFast] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferAmount, setTransferAmount] = useState(10);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [walletRes, txRes] = await Promise.all([
          walletApi.getBalance(),
          walletApi.getTransactions(20)
        ]);
        setWallet(walletRes.data);
        setTransactions(txRes.data);
      } catch (err) {
        toast.error('Failed to load wallet data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const { data } = await paymentsApi.getStatus(sessionId);
      if (data.payment_status === 'paid') {
        toast.success(`Deposit of $${data.amount} successful!`);
        const walletRes = await walletApi.getBalance();
        setWallet(walletRes.data);
        const txRes = await walletApi.getTransactions(20);
        setTransactions(txRes.data);
      }
    } catch (err) {
      console.error('Payment check failed:', err);
    }
  };

  const handleDeposit = async () => {
    if (depositAmount < 5) {
      toast.error('Minimum deposit is $5');
      return;
    }
    setProcessing(true);
    try {
      const { data } = await paymentsApi.createDeposit({
        amount: depositAmount,
        currency: 'usd',
        origin_url: window.location.origin
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate deposit');
      setProcessing(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferEmail || transferAmount < 1) {
      toast.error('Please enter valid email and amount');
      return;
    }
    setProcessing(true);
    try {
      await walletApi.transferQbean({
        recipient_email: transferEmail,
        amount: transferAmount
      });
      toast.success('Qbean transfer successful!');
      setShowTransfer(false);
      setTransferEmail('');
      setTransferAmount(10);
      const [walletRes, txRes] = await Promise.all([
        walletApi.getBalance(),
        walletApi.getTransactions(20)
      ]);
      setWallet(walletRes.data);
      setTransactions(txRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col pb-20 md:pb-0">
      <Header />
      
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-black uppercase text-white mb-8" style={{ fontFamily: 'Outfit' }} data-testid="wallet-title">
          My <span className="text-[#D4AF37]">Wallet</span>
        </h1>

        {/* Balances */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="card-dark" data-testid="real-balance-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #28A745 0%, #1E7E34 100%)' }}>
                <DollarSign className="h-6 w-6 text-white" />
              </div>
              <div>
                <div className="text-sm text-gray-400 font-semibold uppercase">Wallet Balance</div>
                <div className="text-3xl font-black text-[#28A745]" style={{ fontFamily: 'Outfit' }}>
                  ${wallet?.real_balance?.toFixed(2) || '0.00'}
                </div>
              </div>
            </div>
            <Button 
              onClick={() => setShowDeposit(true)} 
              className="btn-green w-full text-sm"
              data-testid="deposit-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Deposit
            </Button>
          </div>

          <div className="card-dark" data-testid="qbean-balance-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #B8960C 100%)' }}>
                <Sparkles className="h-6 w-6 text-[#1A1A1A]" />
              </div>
              <div>
                <div className="text-sm text-gray-400 font-semibold uppercase">Qbean Tokens</div>
                <div className="text-3xl font-black text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>
                  {wallet?.qbean_balance?.toFixed(0) || '0'} QBC
                </div>
              </div>
            </div>
            <Button 
              onClick={() => setShowTransfer(true)} 
              className="btn-primary w-full text-sm"
              data-testid="transfer-btn"
            >
              <Send className="h-4 w-4 mr-2" /> Buy Qbean Tokens
            </Button>
          </div>

          <div className="card-dark" data-testid="escrow-balance-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-[#2D2D2D] border border-[#D4AF37]/30">
                <Wallet className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <div>
                <div className="text-sm text-gray-400 font-semibold uppercase">In Escrow</div>
                <div className="text-3xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
                  ${wallet?.escrow_locked?.toFixed(2) || '0.00'}
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500">Locked in active bets</p>
          </div>
        </div>

        {/* Deposit Modal */}
        {showDeposit && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="card-dark w-full max-w-md my-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Deposit Funds</h2>
                <button onClick={() => setShowDeposit(false)} className="text-gray-400 hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="font-semibold text-white">Amount (USD)</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <Input
                      type="number"
                      min="5"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(parseFloat(e.target.value) || 0)}
                      className="input-dark"
                      style={{ paddingLeft: '2.5rem' }}
                      data-testid="deposit-amount-input"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  {[10, 25, 50, 100].map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setDepositAmount(amt)}
                      className={`flex-1 py-2 rounded-lg border-2 font-semibold transition-all ${
                        depositAmount === amt 
                          ? 'bg-[#D4AF37] text-[#1A1A1A] border-[#D4AF37]' 
                          : 'bg-[#2D2D2D] text-white border-[#3D3D3D] hover:border-[#D4AF37]'
                      }`}
                    >
                      ${amt}
                    </button>
                  ))}
                </div>

                <div className="border-t border-[#3D3D3D] pt-4">
                  <Label className="font-semibold text-white text-sm mb-3 block">Select Payment Method</Label>
                  
                  {/* PayPal - Coming Soon */}
                  <button 
                    onClick={() => toast.info('PayPal integration coming soon!')}
                    className="w-full mb-2 p-3 rounded-lg border-2 border-[#3D3D3D] bg-[#2D2D2D] text-white flex items-center justify-between hover:border-[#0070BA] transition-colors"
                    data-testid="pay-paypal"
                  >
                    <span className="font-semibold text-[#0070BA]">PayPal</span>
                    <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded">Coming Soon</span>
                  </button>
                  
                  {/* Stripe - Coming Soon */}
                  <button 
                    onClick={() => toast.info('Stripe integration coming soon!')}
                    className="w-full mb-2 p-3 rounded-lg border-2 border-[#3D3D3D] bg-[#2D2D2D] text-white flex items-center justify-between hover:border-[#635BFF] transition-colors"
                    data-testid="pay-stripe"
                  >
                    <span className="font-semibold text-[#635BFF]">Stripe</span>
                    <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded">Coming Soon</span>
                  </button>
                  
                  {/* PayFast */}
                  <button 
                    onClick={() => setShowPayFast(true)}
                    className="w-full p-3 rounded-lg border-2 border-[#28A745] bg-[#28A745]/10 text-white flex items-center justify-between hover:bg-[#28A745]/20 transition-colors"
                    disabled={depositAmount < 5}
                    data-testid="pay-payfast"
                  >
                    <span className="font-semibold text-[#28A745]">PayFast</span>
                    <span className="text-xs text-white font-bold">${depositAmount.toFixed(2)}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PayFast Sandbox Modal */}
        {showPayFast && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="card-dark w-full max-w-md my-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#28A745] rounded flex items-center justify-center text-white font-bold text-xs">PF</div>
                  <h2 className="text-lg font-bold text-white">PayFast Checkout</h2>
                </div>
                <button onClick={() => setShowPayFast(false)} className="text-gray-400 hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="text-xs text-[#D4AF37] bg-[#D4AF37]/10 rounded p-2 mb-4 text-center font-semibold">SANDBOX MODE - Test Environment</div>
              
              <div className="space-y-3">
                <div className="bg-[#2D2D2D] p-3 rounded-lg flex justify-between">
                  <span className="text-gray-400">Amount:</span>
                  <span className="text-white font-bold">${depositAmount.toFixed(2)} USD</span>
                </div>
                <div>
                  <Label className="text-white text-sm">Name on Card</Label>
                  <Input placeholder="John Doe" className="input-dark mt-1" data-testid="payfast-name" />
                </div>
                <div>
                  <Label className="text-white text-sm">Card Number</Label>
                  <Input placeholder="4000 0000 0000 0002" className="input-dark mt-1" maxLength={19} data-testid="payfast-card" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white text-sm">Expiry</Label>
                    <Input placeholder="MM/YY" className="input-dark mt-1" maxLength={5} data-testid="payfast-expiry" />
                  </div>
                  <div>
                    <Label className="text-white text-sm">CVV</Label>
                    <Input placeholder="123" className="input-dark mt-1" maxLength={3} type="password" data-testid="payfast-cvv" />
                  </div>
                </div>
                <Button 
                  onClick={() => { 
                    toast.success(`PayFast sandbox: $${depositAmount.toFixed(2)} payment processed!`);
                    setShowPayFast(false);
                    setShowDeposit(false);
                    handleDeposit();
                  }}
                  className="btn-green w-full"
                  data-testid="payfast-submit"
                >
                  Pay ${depositAmount.toFixed(2)}
                </Button>
                <p className="text-xs text-gray-500 text-center">Secured by PayFast.co.za. Test card: 4000 0000 0000 0002</p>
              </div>
            </div>
          </div>
        )}

        {/* Buy Qbean Tokens Modal */}
        {showTransfer && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="card-dark w-full max-w-lg my-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Buy Qbean Tokens (QBC)</h2>
                <button onClick={() => setShowTransfer(false)} className="text-gray-400 hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Current Balance */}
              <div className="bg-[#2D2D2D] rounded-lg p-4 mb-6 flex items-center gap-3">
                <img src="https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/d2e0wwjx_2.png" alt="QBC" className="w-10 h-10" />
                <div>
                  <div className="text-xs text-gray-400">Your QBC Balance</div>
                  <div className="text-2xl font-black text-[#8B8BF5]">{wallet?.qbean_balance?.toFixed(0) || '0'} QBC</div>
                </div>
              </div>

              {/* Buy with Crypto */}
              <div className="mb-6">
                <h3 className="text-sm font-bold text-[#D4AF37] uppercase mb-3">Buy with Crypto</h3>
                <p className="text-xs text-gray-400 mb-3">Select a token to pair with QBC. Send payment to our wallet address below.</p>
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {[
                    { id: 'eth', name: 'ETH', color: '#627EEA' },
                    { id: 'btc', name: 'BTC', color: '#F7931A' },
                    { id: 'usdt', name: 'USDT', color: '#26A17B' },
                    { id: 'sol', name: 'SOL', color: '#9945FF' },
                    { id: 'bnb', name: 'BNB', color: '#F3BA2F' },
                  ].map((token) => (
                    <button
                      key={token.id}
                      onClick={() => setTransferEmail(token.id)}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        transferEmail === token.id 
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                          : 'border-[#3D3D3D] bg-[#2D2D2D] hover:border-[#D4AF37]/50'
                      }`}
                      data-testid={`crypto-${token.id}`}
                    >
                      <div className="text-sm font-bold" style={{ color: token.color }}>{token.name}</div>
                    </button>
                  ))}
                </div>

                {/* Wallet Address */}
                <div className="bg-[#0F0F0F] rounded-lg p-4 border border-[#3D3D3D]">
                  <div className="text-xs text-gray-400 mb-2">QBC Contract / Payment Address</div>
                  <div className="flex items-center gap-2">
                    <code className="text-[#8B8BF5] text-xs flex-1 break-all font-mono">0x7Qb34...C8f2eA91dK</code>
                    <button 
                      onClick={() => { navigator.clipboard.writeText('0x7Qb34C8f2eA91dK'); toast.success('Address copied!'); }}
                      className="bg-[#2D2D2D] p-2 rounded hover:bg-[#3D3D3D] transition-colors"
                      data-testid="copy-address-btn"
                    >
                      <CreditCard className="h-4 w-4 text-[#D4AF37]" />
                    </button>
                  </div>
                </div>

                {/* Connect Wallet */}
                <Button 
                  className="w-full mt-3 bg-[#6366F1] text-white hover:bg-[#5558E6] transition-all"
                  onClick={() => toast.info('MetaMask wallet connection coming soon!')}
                  data-testid="connect-wallet-btn"
                >
                  Connect MetaMask / External Wallet
                </Button>
              </div>

              {/* Buy with Card */}
              <div className="border-t border-[#3D3D3D] pt-6 mb-4">
                <h3 className="text-sm font-bold text-[#D4AF37] uppercase mb-3">Buy with Credit / Debit Card</h3>
                <p className="text-xs text-gray-400 mb-3">Purchase QBC directly with your card. We'll credit your account instantly.</p>
                
                <div>
                  <Label className="font-semibold text-white text-sm">Amount (QBC)</Label>
                  <Input
                    type="number"
                    min="10"
                    value={transferAmount}
                    onChange={(e) => setTransferAmount(parseFloat(e.target.value) || 0)}
                    className="input-dark mt-1"
                    data-testid="buy-qbc-amount"
                  />
                </div>
                <div className="flex gap-2 mt-2 mb-4">
                  {[100, 500, 1000, 5000].map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setTransferAmount(amt)}
                      className={`flex-1 py-2 rounded-lg border-2 font-semibold text-sm transition-all ${
                        transferAmount === amt 
                          ? 'bg-[#D4AF37] text-[#1A1A1A] border-[#D4AF37]' 
                          : 'bg-[#2D2D2D] text-white border-[#3D3D3D] hover:border-[#D4AF37]'
                      }`}
                    >
                      {amt} QBC
                    </button>
                  ))}
                </div>
                <div className="bg-[#2D2D2D] rounded-lg p-3 mb-4 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>Price per QBC:</span>
                    <span className="text-white font-bold">$0.10</span>
                  </div>
                  <div className="flex justify-between text-gray-400 mt-1">
                    <span>Total:</span>
                    <span className="text-[#28A745] font-bold">${(transferAmount * 0.10).toFixed(2)}</span>
                  </div>
                </div>
                <Button 
                  className="btn-green w-full"
                  onClick={() => toast.info('Card payment integration coming soon!')}
                  disabled={transferAmount < 10}
                  data-testid="buy-with-card-btn"
                >
                  <CreditCard className="h-4 w-4 mr-2" /> Pay ${(transferAmount * 0.10).toFixed(2)} with Card
                </Button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                Secured payment portal. All transactions are encrypted and verified.
              </p>
            </div>
          </div>
        )}

        {/* Transaction History */}
        <div className="card-dark">
          <div className="flex items-center gap-2 mb-4">
            <History className="h-5 w-5 text-[#D4AF37]" />
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Transaction History</h2>
          </div>

          {transactions.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No transactions yet</p>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx, idx) => (
                <div key={tx.id || idx} className="flex items-center justify-between py-3 border-b border-[#2D2D2D] last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      tx.amount > 0 ? 'bg-[#28A745]/10' : 'bg-[#D4AF37]/10'
                    }`}>
                      {tx.amount > 0 ? (
                        <ArrowDownLeft className="h-5 w-5 text-[#28A745]" />
                      ) : (
                        <ArrowUpRight className="h-5 w-5 text-[#D4AF37]" />
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-white">{tx.description}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(tx.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className={`font-bold ${tx.amount > 0 ? 'text-[#28A745]' : 'text-[#D4AF37]'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.currency === 'qbean' ? `${tx.amount} QBC` : `$${Math.abs(tx.amount).toFixed(2)}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default WalletPage;
